/**
 * Announcement Center
 * ----------------------------------------------------------------------
 * Vanilla, dependency-free custom element. No jQuery, no globals leaked
 * beyond the one custom element definition.
 *
 * Responsibilities:
 *  - Decide whether this visitor has already dismissed this announcement
 *    (localStorage) and whether it matches their device.
 *  - Show the announcement after the merchant-configured delay.
 *  - Manage focus trap, ESC, overlay click, close button, return focus.
 *  - Persist "don't show again" per the merchant-configured duration.
 *  - Drive the optional countdown timer and its expiration action.
 *
 * Extending this file:
 *  - Analytics / A-B testing: listen for the CustomEvents dispatched
 *    below ('announcement:show', 'announcement:close', 'announcement:cta-click')
 *    from anywhere else in the theme — no need to touch this class.
 *  - New expiration action: add a case to `handleCountdownExpire()`.
 *  - New close trigger: call `this.close(reason)` from anywhere in the class.
 * ----------------------------------------------------------------------
 */
(() => {
  const STORAGE_PREFIX = 'announcement_';
  const MOBILE_BREAKPOINT = 749;

  class AnnouncementCenter extends HTMLElement {
    constructor() {
      super();
      this._countdownInterval = null;
      this._previouslyFocused = null;
      this._onKeydown = this._onKeydown.bind(this);
    }

    connectedCallback() {
      this.announcementId = this.dataset.announcementId || this.id;
      this.dismissDuration = this.dataset.dismissDuration || '14days';
      this.device = this.dataset.device || 'all';
      this.delay = parseFloat(this.dataset.delay || '0.8');

      if (this._isDismissed()) return;
      if (!this._matchesDevice()) return;

      this._panel = this.querySelector('.announcement-center__panel');
      this._overlay = this.querySelector('[data-ac-overlay]');
      this._closeEls = this.querySelectorAll('[data-ac-close]');
      this._dontShowAgainInput = this.querySelector('[data-ac-dont-show-again]');
      this._countdownEl = this.querySelector('[data-ac-countdown]');

      this._bindEvents();

      window.setTimeout(() => this.show(), Math.max(this.delay, 0) * 1000);
    }

    disconnectedCallback() {
      this._stopCountdown();
      document.removeEventListener('keydown', this._onKeydown);
    }

    /* ------------------------------------------------------------------ */
    /* Eligibility helpers                                                 */
    /* ------------------------------------------------------------------ */

    _isDismissed() {
      try {
        const raw = window.localStorage.getItem(STORAGE_PREFIX + this.announcementId);
        if (!raw) return false;
        const data = JSON.parse(raw);
        if (!data.expiresAt) return true; // "forever"
        return Date.now() < data.expiresAt;
      } catch (err) {
        return false; // storage unavailable — fail open, show the announcement
      }
    }

    _matchesDevice() {
      if (this.device === 'all') return true;
      const isMobile = window.innerWidth <= MOBILE_BREAKPOINT;
      return this.device === 'mobile' ? isMobile : !isMobile;
    }

    /* ------------------------------------------------------------------ */
    /* Show / close                                                        */
    /* ------------------------------------------------------------------ */

    show() {
      this._previouslyFocused = document.activeElement;
      this.hidden = false;
      // Force layout so the animation actually plays on first paint.
      void this.offsetWidth;
      this.setAttribute('data-visible', '');

      document.addEventListener('keydown', this._onKeydown);
      document.body.style.overflow = 'hidden';

      const focusTarget = this._panel.querySelector(
        'a, button, input, [tabindex]:not([tabindex="-1"])'
      );
      if (focusTarget) focusTarget.focus();

      this._startCountdown();

      this.dispatchEvent(
        new CustomEvent('announcement:show', {
          bubbles: true,
          detail: { announcementId: this.announcementId },
        })
      );
    }

    close(reason) {
      if (this._dontShowAgainInput && this._dontShowAgainInput.checked) {
        this._persistDismissal();
      }

      this.setAttribute('data-closing', '');
      document.removeEventListener('keydown', this._onKeydown);
      document.body.style.overflow = '';

      const finish = () => {
        this.hidden = true;
        this.removeAttribute('data-visible');
        this.removeAttribute('data-closing');
        if (this._previouslyFocused && typeof this._previouslyFocused.focus === 'function') {
          this._previouslyFocused.focus();
        }
      };

      const prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
      if (prefersReducedMotion) {
        finish();
      } else {
        this._panel.addEventListener('animationend', finish, { once: true });
        // Safety net in case the animation event never fires.
        window.setTimeout(finish, 400);
      }

      this.dispatchEvent(
        new CustomEvent('announcement:close', {
          bubbles: true,
          detail: { announcementId: this.announcementId, reason: reason || 'unknown' },
        })
      );
    }

    _persistDismissal() {
      let expiresAt = null;
      const now = Date.now();

      switch (this.dismissDuration) {
        case 'today': {
          const midnight = new Date();
          midnight.setHours(23, 59, 59, 999);
          expiresAt = midnight.getTime();
          break;
        }
        case '14days':
          expiresAt = now + 14 * 24 * 60 * 60 * 1000;
          break;
        case 'until_expires': {
          const countdownTarget = this._countdownEl ? this._parseTarget(this._countdownEl.dataset.target) : null;
          expiresAt = countdownTarget || now + 30 * 24 * 60 * 60 * 1000;
          break;
        }
        case 'forever':
        default:
          expiresAt = null;
          break;
      }

      try {
        window.localStorage.setItem(
          STORAGE_PREFIX + this.announcementId,
          JSON.stringify({ dismissedAt: now, expiresAt })
        );
      } catch (err) {
        /* localStorage unavailable (private mode, quota, etc). Fail silently. */
      }
    }

    /* ------------------------------------------------------------------ */
    /* Events / focus trap                                                 */
    /* ------------------------------------------------------------------ */

    _bindEvents() {
      this._closeEls.forEach((el) => {
        el.addEventListener('click', (event) => {
          const isCtaWithUrl = el.hasAttribute('data-ac-button') && el.tagName === 'A';
          if (!isCtaWithUrl) event.preventDefault();
          this.close(el.hasAttribute('data-ac-button') ? 'cta' : 'close-button');
        });
      });

      if (this._overlay) {
        this._overlay.addEventListener('click', () => this.close('overlay'));
      }

      const ctaLink = this.querySelector('[data-ac-button][href]');
      if (ctaLink) {
        ctaLink.addEventListener('click', () => {
          this.dispatchEvent(
            new CustomEvent('announcement:cta-click', {
              bubbles: true,
              detail: { announcementId: this.announcementId, url: ctaLink.href },
            })
          );
        });
      }
    }

    _onKeydown(event) {
      if (event.key === 'Escape') {
        this.close('escape');
        return;
      }
      if (event.key === 'Tab') {
        this._trapFocus(event);
      }
    }

    _trapFocus(event) {
      const focusable = this._panel.querySelectorAll(
        'a[href], button:not([disabled]), input:not([disabled]), [tabindex]:not([tabindex="-1"])'
      );
      if (!focusable.length) return;

      const first = focusable[0];
      const last = focusable[focusable.length - 1];

      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    }

    /* ------------------------------------------------------------------ */
    /* Countdown                                                           */
    /* ------------------------------------------------------------------ */

    _parseTarget(value) {
      if (!value) return null;
      const normalized = value.trim().replace(' ', 'T');
      const ts = new Date(normalized).getTime();
      return Number.isNaN(ts) ? null : ts;
    }

    _startCountdown() {
      if (!this._countdownEl) return;

      const targetTs = this._parseTarget(this._countdownEl.dataset.target);
      if (!targetTs) {
        this._countdownEl.hidden = true;
        return;
      }

      const tick = () => {
        const diff = targetTs - Date.now();
        if (diff <= 0) {
          this._stopCountdown();
          this._handleCountdownExpire();
          return;
        }
        this._renderCountdown(diff);
      };

      tick();
      this._countdownInterval = window.setInterval(tick, 1000);
    }

    _stopCountdown() {
      if (this._countdownInterval) {
        window.clearInterval(this._countdownInterval);
        this._countdownInterval = null;
      }
    }

    _renderCountdown(diffMs) {
      const totalSeconds = Math.floor(diffMs / 1000);
      const days = Math.floor(totalSeconds / 86400);
      const hours = Math.floor((totalSeconds % 86400) / 3600);
      const minutes = Math.floor((totalSeconds % 3600) / 60);
      const seconds = totalSeconds % 60;

      const pad = (n) => String(n).padStart(2, '0');

      const daysEl = this.querySelector('[data-ac-days]');
      const hoursEl = this.querySelector('[data-ac-hours]');
      const minutesEl = this.querySelector('[data-ac-minutes]');
      const secondsEl = this.querySelector('[data-ac-seconds]');

      if (daysEl) daysEl.textContent = pad(days);
      if (hoursEl) hoursEl.textContent = pad(hours);
      if (minutesEl) minutesEl.textContent = pad(minutes);
      if (secondsEl) secondsEl.textContent = pad(seconds);
    }

    _handleCountdownExpire() {
      const action = this._countdownEl.dataset.expireAction || 'hide_countdown';

      switch (action) {
        case 'hide_countdown':
          this._countdownEl.hidden = true;
          break;

        case 'hide_announcement':
          this.close('countdown-expired');
          break;

        case 'replace_button': {
          const label = this._countdownEl.dataset.expireButtonLabel;
          const buttonLabelEl = this.querySelector('[data-ac-button-label]');
          if (label && buttonLabelEl) buttonLabelEl.textContent = label;
          this._countdownEl.hidden = true;
          break;
        }

        case 'replace_message': {
          const expireMessageEl = this.querySelector('[data-ac-expire-message]');
          const contentEl = this.querySelector('.announcement-center__content');
          if (expireMessageEl) {
            expireMessageEl.hidden = false;
            if (contentEl) contentEl.hidden = true;
          }
          this._countdownEl.hidden = true;
          break;
        }

        case 'redirect': {
          const url = this._countdownEl.dataset.expireRedirect;
          if (url) window.location.href = url;
          break;
        }

        default:
          this._countdownEl.hidden = true;
      }

      this.dispatchEvent(
        new CustomEvent('announcement:countdown-expired', {
          bubbles: true,
          detail: { announcementId: this.announcementId, action },
        })
      );
    }
  }

  if (!customElements.get('announcement-center')) {
    customElements.define('announcement-center', AnnouncementCenter);
  }
})();